import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { ROUTES, getRouteByPath } from '../../app/routes';
import { ELEMENT_CATEGORIES } from '../../shared/panels/mesh-panel-constants';
import LayerUtils, { fetchLayer } from '../../shared/layers/layer-utils';
import { MmgPanelHeader } from '../../shared/panels/panel-header';
import { t } from '../../translations/i18n';
import { MmgConnectedVariableOperationsSection } from './variable-operation-section';
import { IDrawnDataItem } from '../../models/IWorkspaceData';
import { LinearProgress } from '@mui/material';
import { getVariableOperationPanelDescription } from '../../shared/panels/panel-utils';
import WorkspaceVariableSelectors from '../../store/selectors/WorkspaceVariableSelectors';
import { IWorkspaceEnrichedVariable } from '../../models/IVariables';
import { IGlobalState } from '../../store/reducers';
import MikeStickyPanel from '../../shared-components/mike-sticky-panel';
import { MikeStickyPanelHeaderContainer } from '../../shared-components/mike-sticky-panel/MikeStickyPanelHeaderContainer';

const { hideOtherLayers, isLayerWorking } = LayerUtils;

/**
 * @name MmgConnectedVariableOperationPanel
 * @summary Allows performing 'geometry operation' on the current geometry, former referred to as 'transforms'.
 *
 * If the geometry does not exists, it will redirect to geometry details panel
 *
 */
export const MmgConnectedVariableOperationPanel = () => {
  const { workspaceId, projectId, variableId } = useParams();
  const navigate = useNavigate();
  const getVariableSelectorInstance = WorkspaceVariableSelectors.makeGetVariable();
  const getDrawnDataSelectorInstance = WorkspaceVariableSelectors.makeGetVariableDrawnData();
  const loadedData: Array<string> = useSelector((state: IGlobalState) => state.WorkspaceDataReducer.loadedData);
  const variable: IWorkspaceEnrichedVariable = useSelector((state: IGlobalState) =>
    getVariableSelectorInstance(state, { variableId }),
  );
  const variableDrawnData: IDrawnDataItem = useSelector((state: IGlobalState) =>
    getDrawnDataSelectorInstance(state, { variableId }),
  );

  const title = t('VARIABLE_OPERATION_PANEL');

  // When an operation is submitted succesfully we currently navigate to workspace panel
  const onOperationSubmitted = (success: boolean) => {
    if (success) {
      // always go to workspace panel on success, since all geoemtry operations currently will create a new geometry
      navigate(getRouteByPath(ROUTES.workspacePanel.path, { workspaceId, projectId }, ROUTES.workspace.path));  
    }
  };

  // todo hevo show/hide items should be as in geoemtry detail
  // make sure the only item shown in the viewer is the geometry at hand when the geometry is loaded
  useEffect(
    () => {
      if (!loadedData.includes(variableId)) {
        fetchLayer(variableId, ELEMENT_CATEGORIES.VARIABLE);
      }
      if (variable && variable.id) {
        hideOtherLayers(ELEMENT_CATEGORIES.VARIABLE, variable.id);
      }
    },
    [loadedData, variable, variableId],
  );

  if (!variable) {
    navigate(getRouteByPath(ROUTES.variableDetailsPanel.path, { workspaceId, projectId, variableId }, ROUTES.workspace.path));
  }

  const variableIds = variableId ? [variableId] : [];
  const drawnVariables = variableDrawnData ? [variableDrawnData] : [];
  const isVariableWorking = isLayerWorking(variable, drawnVariables);

  const description = getVariableOperationPanelDescription(variable, drawnVariables);

  return (
    <MikeStickyPanel>
      <MikeStickyPanelHeaderContainer>
        <MmgPanelHeader panelTitle={title} panelDescription={description} />
        {isVariableWorking && <LinearProgress />}
      </MikeStickyPanelHeaderContainer>

      <MmgConnectedVariableOperationsSection
        projectId={projectId}
        workspaceId={workspaceId}
        variableIds={variableIds}
        onOperationSubmitted={onOperationSubmitted}
      />
    </MikeStickyPanel>
  );
};
